import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ProductionTable from '../my-production/ProductionTable';
import AutoRefreshCountdown from '../common/AutoRefreshCountdown';
import StationNotesModal, { StationProblemType } from './StationNotesModal';

interface WarehouseItem {
  id: number;
  przewinieta: boolean;
  rewound_z1: number;
  rewound_z2: number;
  przewinieta_at: string | null;
  // Uwaga stacji przewijania — niezależna od uwagi linii.
  notes?: string | null;
  notes_by?: string | null;
  notes_at?: string | null;
  przewinieta_by: string | null;
  // The people who rewound the spools, in the order it happened.
  rewound_by?: string[];
  completed_by: string | null;
  created_at: string;
  production_item_id: number;
  production_assignment_id: number;
  linia: string | null;
  tydzien: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
  ean: string | null;
  zmiana_1: string | null;
  zmiana_2: string | null;
  uwagi: string | null;
  barwnik_dostarczony?: boolean;
  priorytet?: boolean;
  // Pieces produced on the line and whether the line is done with the position — only then
  // may the station mark it as rewound (the backend refuses earlier).
  produced_on_line?: number;
  line_finished?: boolean;
}

interface ConfirmModalState {
  item: WarehouseItem;
  loading: boolean;
}

interface RewindModalState {
  item: WarehouseItem;
  shift: 1 | 2;
  value: string;
  saving: boolean;
}

export default function LargeWarehouseView() {
  const { isManager, user } = useAuth();
  // Cofnięcie przewinięcia zmienia nadwyżki materiałowe — dostępne tylko dla admina.
  const canRestoreRewinding = user?.role === 'admin';
  const { t, language } = useLanguage();
  const dateLocale = language === 'en' ? 'en-GB' : language === 'ru' ? 'uk-UA' : 'pl-PL';
  const [items, setItems] = useState<WarehouseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<ConfirmModalState | null>(null);
  // Przywrócenie omyłkowo zakończonej pozycji z „Przewinięte" z powrotem do przewijania.
  const [restoreItem, setRestoreItem] = useState<WarehouseItem | null>(null);
  const [restoring, setRestoring] = useState(false);
  // Cofnięcie oczekującej pozycji ze stacji z powrotem do „Mojej produkcji".
  const [removeItem, setRemoveItem] = useState<WarehouseItem | null>(null);
  const [removing, setRemoving] = useState(false);
  // Uwagi stacji (własne, nie z linii) — modal + lista typów problemów.
  const [notesItem, setNotesItem] = useState<WarehouseItem | null>(null);
  const [problemTypes, setProblemTypes] = useState<StationProblemType[]>([]);
  // Po osiągnięciu planu (przewinięto >= plan) pytamy, czy oznaczyć szpulę jako przewiniętą.
  const [markRewoundChoice, setMarkRewoundChoice] = useState<WarehouseItem | null>(null);
  const [rewindModal, setRewindModal] = useState<RewindModalState | null>(null);
  const [rewindHistory, setRewindHistory] = useState<any[]>([]);
  const [loadingRewindHistory, setLoadingRewindHistory] = useState(false);
  const [negativeConfirm, setNegativeConfirm] = useState(false);
  const [negativeHoldProgress, setNegativeHoldProgress] = useState(0);
  const negativeHoldStartRef = useRef<number | null>(null);
  const negativeHoldTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [scanItem, setScanItem] = useState<WarehouseItem | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scannerReady, setScannerReady] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'qr-scanner-container';
  // Line filter (several lines can be selected, empty = all) — narrows both tables.
  const [lineFilter, setLineFilter] = useState<string[]>([]);
  // Week filter (several weeks can be selected, empty = all) — narrows both tables, independent of the line filter.
  const [weekFilter, setWeekFilter] = useState<string[]>([]);
  // Search box — narrows both tables and the per-line summary like the week filter.
  const [search, setSearch] = useState('');
  const [wagaProg, setWagaProg] = useState<string>('20');
  const [savingProg, setSavingProg] = useState(false);
  const [progInput, setProgInput] = useState<string>('20');

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/warehouse/large');
      setItems(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await api.get('/settings');
      const prog = res.data?.waga_prog?.value ?? '20';
      setWagaProg(prog);
      setProgInput(prog);
    } catch {
      // silent
    }
  }, [isManager]);

  useEffect(() => {
    fetchItems();
    fetchSettings();
    api.get('/problem-types').then((res) => setProblemTypes(res.data || [])).catch(() => setProblemTypes([]));
  }, [fetchItems, fetchSettings]);

  const handleRowClick = async (item: WarehouseItem) => {
    if (item.przewinieta) return;
    setConfirm({ item, loading: item.line_finished === false });
    if (item.line_finished !== false) return;
    // The list refreshes only every 10 minutes — the line may have finished the plan since,
    // so re-check before keeping "YES" blocked.
    try {
      const res = await api.get('/warehouse/large');
      setItems(res.data);
      const fresh = (res.data as WarehouseItem[]).find((i) => i.id === item.id);
      setConfirm((c) => (c && c.item.id === item.id
        ? { item: fresh ? { ...c.item, line_finished: fresh.line_finished, produced_on_line: fresh.produced_on_line } : c.item, loading: false }
        : c));
    } catch {
      setConfirm((c) => (c && c.item.id === item.id ? { ...c, loading: false } : c));
    }
  };

  // Przywraca zakończoną pozycję do przewijania (np. gdy ktoś omyłkowo zamknął pozycję po
  // wpisaniu nadwyżki). Backend kasuje znacznik „przewinięta", przez co pozycja znika
  // z nadwyżek i wraca na niebiesko do oczekujących — ilość poprawia się tu ręcznie.
  const handleRestoreConfirm = async () => {
    if (!restoreItem) return;
    setRestoring(true);
    try {
      await api.post(`/warehouse/large/${restoreItem.id}/restore`);
      await fetchItems();
      setRestoreItem(null);
    } catch (err: any) {
      alert(err?.response?.data?.message || t('largeWarehouse.restoreError'));
    } finally {
      setRestoring(false);
    }
  };

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* ignore */ }
      try { scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    await stopScanner();
    await new Promise(r => setTimeout(r, 300));
    const el = document.getElementById(scannerContainerId);
    if (!el) return;

    const scanner = new Html5Qrcode(scannerContainerId);
    scannerRef.current = scanner;
    const config = { fps: 10, qrbox: { width: 250, height: 250 } };

    const onScanSuccess = (decodedText: string) => {
      try {
        const data = JSON.parse(decodedText);
        if (data.id && scanItem && data.id === scanItem.production_item_id) {
          stopScanner();
          const itemToMark = scanItem;
          setScanItem(null);
          setScanError(null);
          markAsRewound(itemToMark);
        } else {
          setScanError(
            t('largeWarehouse.scanQrMismatch', {
              expected: String(scanItem?.indeks_kat || scanItem?.production_item_id),
              got: String(data.indeks_kat || data.indeks || data.id),
            })
          );
        }
      } catch {
        setScanError(t('largeWarehouse.scanQrInvalid'));
      }
    };
    const onScanFailure = () => { /* ignore scan failures */ };

    // Preferuj tylną kamerę (environment); jeśli brak — przednia (user).
    const attempts: any[] = [
      { facingMode: { exact: 'environment' } },
      { facingMode: 'environment' },
      { facingMode: 'user' },
    ];
    for (const camConfig of attempts) {
      try {
        await scanner.start(camConfig, config, onScanSuccess, onScanFailure);
        setScannerReady(true);
        return;
      } catch {
        // spróbuj następnej opcji kamery
      }
    }

    // Fallback: wybór kamery z listy urządzeń (preferuj tylną po etykiecie).
    try {
      const cameras = await Html5Qrcode.getCameras();
      if (cameras.length === 0) {
        setScanError(t('largeWarehouse.scanCameraNoDevices'));
        return;
      }
      const backCam = cameras.find(c => c.label.toLowerCase().includes('back') || c.label.toLowerCase().includes('environment'));
      const cameraId = backCam?.id || cameras[0].id;
      await scanner.start(cameraId, config, onScanSuccess, onScanFailure);
      setScannerReady(true);
    } catch (err: any) {
      setScanError(t('largeWarehouse.scanCameraStartError') + ': ' + (err?.message || ''));
    }
  }, [scanItem, stopScanner]);

  useEffect(() => {
    if (scanItem) {
      startScanner();
    }
    return () => { stopScanner(); };
  }, [scanItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScanCancel = async () => {
    await stopScanner();
    setScanItem(null);
    setScanError(null);
  };

  const handleConfirmYes = () => {
    if (!confirm || confirm.item.line_finished === false) return;
    // TEMP: wymóg zeskanowania QR przed zatwierdzeniem przewinięcia jest tymczasowo wyłączony.
    // Zamiast otwierać skaner, oznaczamy szpulę od razu jako przewiniętą.
    // Aby przywrócić skanowanie: odkomentuj poniższy blok i usuń wywołanie markAsRewound().
    // setScanItem(confirm.item);
    // setScanError(null);
    // setScannerReady(false);
    // setConfirm(null);
    const item = confirm.item;
    setConfirm(null);
    markAsRewound(item);
  };

  const markAsRewound = async (item: WarehouseItem) => {
    try {
      await api.post(`/warehouse/large/${item.id}/przewinieta`);
      await fetchItems();
    } catch (err: any) {
      alert(err?.response?.data?.message || t('largeWarehouse.markRewoundError'));
    }
  };

  // Zdejmuje oczekującą pozycję ze stacji — wpis magazynowy znika, więc wiersz na linii
  // przestaje być niebieski i pozycja wraca do zwykłego obiegu „Mojej produkcji".
  const handleRemoveConfirm = async () => {
    if (!removeItem) return;
    setRemoving(true);
    try {
      await api.delete(`/warehouse/large/${removeItem.id}`);
      await fetchItems();
      setRemoveItem(null);
    } catch (err: any) {
      alert(err?.response?.data?.message || t('largeWarehouse.removeError'));
    } finally {
      setRemoving(false);
    }
  };

  const fetchRewindHistory = async (warehouseItemId: number) => {
    setLoadingRewindHistory(true);
    try {
      const res = await api.get(`/warehouse/large/${warehouseItemId}/rewind-history`);
      setRewindHistory(res.data || []);
    } catch {
      setRewindHistory([]);
    } finally {
      setLoadingRewindHistory(false);
    }
  };

  const openRewindModal = (item: WarehouseItem, shift: 1 | 2) => {
    setRewindModal({ item, shift, value: '', saving: false });
    setNegativeConfirm(false);
    fetchRewindHistory(item.id);
  };

  const closeRewindModal = () => {
    setRewindModal(null);
    setNegativeConfirm(false);
    setRewindHistory([]);
    handleNegativeHoldEnd();
  };

  // Holding for 3 s confirms a negative value — 1:1 like in "My production".
  const handleNegativeHoldStart = () => {
    negativeHoldStartRef.current = Date.now();
    setNegativeHoldProgress(0);
    negativeHoldTimerRef.current = setInterval(() => {
      if (negativeHoldStartRef.current) {
        const elapsed = Date.now() - negativeHoldStartRef.current;
        const progress = Math.min((elapsed / 3000) * 100, 100);
        setNegativeHoldProgress(progress);
        if (elapsed >= 3000) {
          if (negativeHoldTimerRef.current) {
            clearInterval(negativeHoldTimerRef.current);
            negativeHoldTimerRef.current = null;
          }
          negativeHoldStartRef.current = null;
          setNegativeHoldProgress(0);
          handleSaveRewindQuantity(true);
        }
      }
    }, 50);
  };

  const handleNegativeHoldEnd = () => {
    if (negativeHoldTimerRef.current) {
      clearInterval(negativeHoldTimerRef.current);
      negativeHoldTimerRef.current = null;
    }
    negativeHoldStartRef.current = null;
    setNegativeHoldProgress(0);
  };

  // Entries accumulate (+10, +10, -2…) — like quantities in "My production".
  const handleSaveRewindQuantity = async (forceNegative = false) => {
    if (!rewindModal) return;
    const quantity = parseInt(rewindModal.value, 10);
    if (isNaN(quantity) || quantity === 0) return;
    if (quantity < 0 && !forceNegative) {
      setNegativeConfirm(true);
      return;
    }
    setRewindModal({ ...rewindModal, saving: true });
    const savedItem = rewindModal.item;
    const plan = savedItem.do_produkcji ? parseInt(savedItem.do_produkcji, 10) : 0;
    const newTotal = (savedItem.rewound_z1 ?? 0) + (savedItem.rewound_z2 ?? 0) + quantity;
    try {
      await api.post(`/warehouse/large/${rewindModal.item.id}/rewound-quantity`, { quantity, shift: rewindModal.shift });
      await fetchItems();
      closeRewindModal();
      // Osiągnięto/przekroczono plan → zapytaj, czy oznaczyć jako przewiniętą.
      // Pozycja pozostaje niebieska (oczekująca) aż do potwierdzenia.
      if (plan > 0 && newTotal >= plan && savedItem.line_finished !== false) {
        setMarkRewoundChoice(savedItem);
      }
    } catch (err: any) {
      if (err?.response?.data?.message) alert(err.response.data.message);
      setNegativeConfirm(false);
      setRewindModal((m) => (m ? { ...m, saving: false } : m));
    }
  };

  const handleSaveProg = async () => {
    setSavingProg(true);
    try {
      await api.put('/settings/waga_prog', { value: progInput });
      setWagaProg(progInput);
    } finally {
      setSavingProg(false);
    }
  };

  const lineOf = (item: WarehouseItem) => item.linia?.trim() || t('largeWarehouse.lineUnknown');
  const weekOf = (item: WarehouseItem) => item.tydzien?.trim() || t('largeWarehouse.weekUnknown');

  // Weeks present on the station, newest-looking first (numeric part descending when there is one).
  const availableWeeks = Array.from(new Set(items.map(weekOf))).sort((a, b) => {
    const na = parseInt(a.replace(/[^0-9]/g, ''), 10);
    const nb = parseInt(b.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(na) && !isNaN(nb) && na !== nb) return nb - na;
    return a.localeCompare(b);
  });

  // Weeks that disappeared from the station after a refresh no longer filter anything.
  const activeWeeks = weekFilter.filter((w) => availableWeeks.includes(w));
  const matchesWeek = (i: WarehouseItem) => activeWeeks.length === 0 || activeWeeks.includes(weekOf(i));
  const toggleWeek = (week: string) =>
    setWeekFilter((prev) => (prev.includes(week) ? prev.filter((w) => w !== week) : [...prev, week]));

  // Comma / semicolon separated phrases search for several items at once ("PLA-001, PLA-002");
  // within a phrase every word has to appear in one of the searched fields ("pla red" → PLA items in red).
  const searchPhrases = search
    .toLowerCase()
    .split(/[,;]/)
    .map((phrase) => phrase.trim().split(/\s+/).filter(Boolean))
    .filter((words) => words.length > 0);
  const matchesSearch = (i: WarehouseItem) => {
    if (searchPhrases.length === 0) return true;
    const haystack = [i.indeks_kat, i.nazwa_oryg, i.ean, i.kolor, i.material, i.marka]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchPhrases.some((words) => words.every((word) => haystack.includes(word)));
  };

  // Lines that disappeared from the station after a refresh no longer filter anything.
  const availableLines = new Set(items.map(lineOf));
  const activeLines = lineFilter.filter((l) => availableLines.has(l));
  const matchesLine = (i: WarehouseItem) => activeLines.length === 0 || activeLines.includes(lineOf(i));
  const toggleLine = (line: string) =>
    setLineFilter((prev) => (prev.includes(line) ? prev.filter((l) => l !== line) : [...prev, line]));

  const allPending = items.filter((i) => !i.przewinieta).filter(matchesWeek).filter(matchesSearch);
  const pending = allPending.filter(matchesLine);
  const done = items
    .filter((i) => i.przewinieta)
    .filter(matchesWeek)
    .filter(matchesSearch)
    .filter(matchesLine);

  // How many large spools await rewinding per production line (sumarycznie).
  // Always counted over every line so the summary stays complete while filtering by line
  // (the week filter does narrow it — the counts describe the selected week).
  // Sorted by count desc so the most loaded line reads first.
  const pendingByLine = Array.from(
    allPending.reduce((acc, item) => {
      acc.set(lineOf(item), (acc.get(lineOf(item)) ?? 0) + 1);
      return acc;
    }, new Map<string, number>())
  ).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  // Selected lines with nothing pending under the current week / search keep a chip (count 0)
  // so they can still be deselected.
  const lineChips: Array<[string, number]> = [
    ...pendingByLine,
    ...activeLines.filter((l) => !pendingByLine.some(([line]) => line === l)).map((l): [string, number] => [l, 0]),
  ];

  // The table is rendered with the ProductionTable component (1:1 like "My production");
  // priority is shown in its own ⭐ column and by a yellow row.
  // Z1/Z2 show per-shift rewound counters (Rewound = Z1 + Z2).
  const toTableItem = (item: WarehouseItem) => ({
    ...item,
    zmiana_1: item.rewound_z1 ? String(item.rewound_z1) : null,
    zmiana_2: item.rewound_z2 ? String(item.rewound_z2) : null,
    // Kolumna uwag pokazuje uwagę STACJI (large_warehouse_items.notes), nigdy uwagi linii.
    pivot: { notes: item.notes ?? null },
    rewound_by_text: (item.rewound_by ?? []).join(', ') || null,
  });
  const noop = () => {};

  const hasActiveFilters = searchPhrases.length > 0 || activeWeeks.length > 0 || activeLines.length > 0;
  const clearFilters = () => {
    setSearch('');
    setWeekFilter([]);
    setLineFilter([]);
  };

  // Filter rows: a fixed label column with the chips wrapping next to it (below it on narrow screens).
  const filterRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' };
  const filterLabelStyle: React.CSSProperties = { width: 150, flexShrink: 0, paddingTop: 6 };
  const chipsStyle: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', flex: '1 1 300px', minWidth: 0 };
  const renderChip = (key: string, label: string, count: number | null, selected: boolean, onClick: () => void) => (
    <button
      key={key}
      onClick={onClick}
      aria-pressed={selected}
      style={{
        display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '6px 12px',
        border: `1px solid ${selected ? '#667eea' : '#e2e8f0'}`, borderRadius: 8,
        background: selected ? '#eef2ff' : '#fff', boxShadow: selected ? 'inset 0 0 0 1px #667eea' : 'none',
        cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.3,
      }}
    >
      <span style={{ fontWeight: 700, color: selected ? '#4c51bf' : '#475569' }}>{label}</span>
      {count !== null && <span style={{ fontSize: 12, color: '#94a3b8' }}>{count}</span>}
    </button>
  );

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>
            🏭 {t('largeWarehouse.title')}
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>
            {t('largeWarehouse.subtitle')}
          </p>
        </div>
        <AutoRefreshCountdown intervalSeconds={600} onRefresh={fetchItems} />
      </div>

      {/* Settings panel (manager only) */}
      {isManager && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>{t('largeWarehouse.weightThresholdLabel')}</span>
          <input
            type="number"
            value={progInput}
            onChange={(e) => setProgInput(e.target.value)}
            style={{
              width: 100, padding: '6px 10px', border: '1px solid #cbd5e1',
              borderRadius: 6, fontSize: 14,
            }}
          />
          <span style={{ fontSize: 13, color: '#94a3b8' }}>{t('largeWarehouse.weightThresholdHelp')}</span>
          <button
            onClick={handleSaveProg}
            disabled={savingProg || progInput === wagaProg}
            style={{
              padding: '6px 16px', background: '#1e40af', color: '#fff',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14,
              opacity: (savingProg || progInput === wagaProg) ? 0.5 : 1,
            }}
          >
            {savingProg ? t('largeWarehouse.saving') : t('largeWarehouse.save')}
          </button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{t('largeWarehouse.currentThreshold')} <strong>{wagaProg}</strong></span>
        </div>
      )}

      {/* Filters — search, weeks and lines narrow both tables and the stats below */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '16px 20px', marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
            {t('largeWarehouse.filtersTitle')}
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: '4px 12px', background: '#667eea', color: '#fff', border: 'none',
                borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ✕ {t('largeWarehouse.clearFilters')}
            </button>
          )}
        </div>

        <div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('largeWarehouse.searchPlaceholder')}
            aria-label={t('largeWarehouse.searchPlaceholder')}
            aria-describedby="large-warehouse-search-hint"
            style={{
              width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 14px',
              border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 16, background: '#fff',
            }}
          />
          <div id="large-warehouse-search-hint" style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
            {t('largeWarehouse.searchHint')}
          </div>
        </div>

        {availableWeeks.length > 1 && (
          <div style={filterRowStyle}>
            <div style={filterLabelStyle}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{t('largeWarehouse.weekFilterLabel')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{t('largeWarehouse.weekFilterHint')}</div>
            </div>
            <div style={chipsStyle}>
              {renderChip('all-weeks', t('largeWarehouse.weekFilterAll'), null, activeWeeks.length === 0, () => setWeekFilter([]))}
              {availableWeeks.map((week) => renderChip(
                week,
                week,
                items.filter((i) => weekOf(i) === week && !i.przewinieta).length,
                activeWeeks.includes(week),
                () => toggleWeek(week),
              ))}
            </div>
          </div>
        )}

        {!loading && lineChips.length > 0 && (
          <div style={filterRowStyle}>
            <div style={filterLabelStyle}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{t('largeWarehouse.lineFilterLabel')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{t('largeWarehouse.lineFilterHint')}</div>
            </div>
            <div style={chipsStyle}>
              {renderChip('all-lines', t('largeWarehouse.lineFilterAll'), null, activeLines.length === 0, () => setLineFilter([]))}
              {lineChips.map(([line, count]) => renderChip(
                line,
                line,
                count,
                activeLines.includes(line),
                () => toggleLine(line),
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          // Oczekuje = niebieski (spójnie z wierszami: niebieski = do przewijania), nie pomarańczowy.
          { label: t('largeWarehouse.statsPending'), value: pending.length, color: '#2563eb', bg: '#eff6ff' },
          { label: t('largeWarehouse.statsDone'), value: done.length, color: '#10b981', bg: '#ecfdf5' },
          { label: t('largeWarehouse.statsTotal'), value: pending.length + done.length, color: '#6366f1', bg: '#eef2ff' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} style={{
            flex: 1, minWidth: 140, background: bg, border: `1px solid ${color}30`,
            borderRadius: 10, padding: '14px 20px',
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>{t('largeWarehouse.loading')}</div>
      ) : pending.length + done.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 48, background: '#f8fafc',
          borderRadius: 12, border: '1px dashed #e2e8f0', color: '#94a3b8',
        }}>
          {searchPhrases.length > 0
            ? t('largeWarehouse.searchEmpty', { q: search.trim() })
            : t('largeWarehouse.noItems')}
        </div>
      ) : (
        <>
          {/* Pending table */}
          {pending.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                ⏳ {t('largeWarehouse.pendingTitle', { count: pending.length })}
              </h3>
              <ProductionTable
                items={pending.map(toTableItem) as any}
                onRowClick={(item: any) => handleRowClick(item)}
                onNotesClick={(_e, item: any) => setNotesItem(item as WarehouseItem)}
                onQuantityClick={(_e, item: any, field) => openRewindModal(item, field === 'zmiana_1' ? 1 : 2)}
                onBarwnikClick={noop}
                // Oczekujące są zawsze niebieskie — nawet po osiągnięciu planu (przewinięto >= plan).
                // Zielone dopiero po ręcznym oznaczeniu jako przewinięte (wtedy trafiają do
                // tabeli „Przewinięte"). Priorytet sygnalizuje kolumna ⭐, nie kolor wiersza.
                getRowColor={() => '#cfe2ff'}
                getItemStatus={() => 'pending'}
                labelOverrides={{ toProduce: t('largeWarehouse.colToRewind'), produced: t('largeWarehouse.colRewoundCount') }}
                showLineColumn
                showPriorityColumn
                showRewoundByColumn
                hideDosageColumn
                hideEanColumn
              />
            </div>
          )}

          {/* Done table */}
          {done.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                ✅ {t('largeWarehouse.doneTitle', { count: done.length })}
              </h3>
              {canRestoreRewinding && (
                <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#64748b' }}>
                  {t('largeWarehouse.restoreHint')}
                </p>
              )}
              <ProductionTable
                items={done.map(toTableItem) as any}
                onRowClick={(item: any) => { if (canRestoreRewinding) setRestoreItem(item as WarehouseItem); }}
                onNotesClick={(_e, item: any) => setNotesItem(item as WarehouseItem)}
                onQuantityClick={noop}
                onBarwnikClick={noop}
                getRowColor={() => '#d4edda'}
                getItemStatus={() => 'completed'}
                labelOverrides={{ toProduce: t('largeWarehouse.colToRewind'), produced: t('largeWarehouse.colRewoundCount') }}
                showLineColumn
                showPriorityColumn
                showRewoundByColumn
                hideDosageColumn
                hideEanColumn
              />
            </div>
          )}
        </>
      )}

      {/* QR Scanner modal */}
      {scanItem && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 480, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.scanModalTitle')}
            </h2>
            <p style={{ margin: '0 0 16px 0', color: '#475569', fontSize: 14 }}>
              {t('largeWarehouse.scanModalSubtitle')} <strong>{scanItem.indeks_kat}</strong> — {scanItem.material}
            </p>

            <div
              id={scannerContainerId}
              style={{
                width: '100%', minHeight: 280, borderRadius: 8, overflow: 'hidden',
                marginBottom: 16, background: '#000',
              }}
            />

            {!scannerReady && !scanError && (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>{t('largeWarehouse.scanCameraStarting')}</p>
            )}

            {scanError && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b',
              }}>
                {scanError}
              </div>
            )}

            <button
              onClick={handleScanCancel}
              style={{
                width: '100%', padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('largeWarehouse.scanCancel')}
            </button>
          </div>
        </div>
      )}

      {/* Rewound quantity modal — works like the quantity modal in "My production": entries accumulate, negatives correct */}
      {rewindModal && (() => {
        const max = rewindModal.item.do_produkcji ? parseInt(rewindModal.item.do_produkcji, 10) : null;
        const z1 = rewindModal.item.rewound_z1 ?? 0;
        const z2 = rewindModal.item.rewound_z2 ?? 0;
        const entered = parseInt(rewindModal.value, 10);
        const isNegative = !isNaN(entered) && entered < 0;
        // Przewinięcie ponad plan jest DOZWOLONE — nadwyżka (przewinięto − plan) trafia
        // do „Nadwyżki materiałowej". Nie blokujemy zapisu; pokazujemy tylko informację.
        const projectedTotal = !isNaN(entered) ? z1 + z2 + entered : z1 + z2;
        const overPlan = max !== null && !isNaN(entered) && entered > 0 && projectedTotal > max;
        const surplusAmount = overPlan ? projectedTotal - (max as number) : 0;
        const saveDisabled = rewindModal.saving || isNaN(entered) || entered === 0;
        const shiftName = rewindModal.shift === 1 ? t('production.shift1') : t('production.shift2');
        const shiftHistory = rewindHistory.filter((e) => e.shift === rewindModal.shift);
        return (
          <div
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: 'white', padding: '30px', borderRadius: '12px',
                maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <h3 style={{ marginTop: 0, color: '#667eea' }}>
                {t('largeWarehouse.rewindModalTitle')} ({shiftName})
              </h3>
              <div style={{
                marginTop: '15px', padding: '12px', backgroundColor: '#e7f3ff',
                border: '1px solid #667eea', borderRadius: '8px', color: '#4a5568', fontSize: '10px',
              }}>
                📊 <strong>{rewindModal.item.indeks_kat}</strong> — {rewindModal.item.material}
                {max !== null && <> | {t('largeWarehouse.rewindModalMax', { max })}</>}
                {' '}| {t('largeWarehouse.rewindModalCurrent')} <strong>{z1 + z2}</strong> ({t('table.z1')}: {z1}, {t('table.z2')}: {z2})
              </div>
              <div style={{ marginTop: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  {t('myProduction.enterQuantityLabel')}
                </label>
                <input
                  type="number"
                  value={rewindModal.value}
                  onChange={(e) => setRewindModal({ ...rewindModal, value: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !saveDisabled) handleSaveRewindQuantity(); }}
                  placeholder="0"
                  autoFocus
                  style={{
                    width: '100%', padding: '12px', boxSizing: 'border-box',
                    border: `2px solid ${isNegative ? '#dc3545' : '#e1e8ed'}`,
                    borderRadius: '8px', fontSize: '16px', backgroundColor: 'white',
                    color: isNegative ? '#dc3545' : 'inherit',
                  }}
                />
                {overPlan && (
                  <div style={{ marginTop: '8px', color: '#2563eb', fontSize: '12px' }}>
                    ℹ️ {t('largeWarehouse.rewindModalSurplusInfo', { surplus: surplusAmount })}
                  </div>
                )}
              </div>

              {/* History Section */}
              <div style={{ marginTop: '30px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#4a5568', fontSize: '16px', fontWeight: '600' }}>
                  📊 {t('myProduction.quantityChangeHistoryTitle', { shift: shiftName })}
                </h4>
                {loadingRewindHistory ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#667eea' }}>
                    {t('myProduction.loadingHistory')}
                  </div>
                ) : shiftHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '10px' }}>
                    {t('myProduction.noHistoryShift')}
                  </div>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e1e8ed' }}>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('myProduction.historyDate')}
                          </th>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('myProduction.historyQuantityColumn')}
                          </th>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('myProduction.historyOperator')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {shiftHistory.map((entry, index) => (
                          <tr
                            key={entry.id || index}
                            style={{
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                            }}
                          >
                            <td style={{ padding: '10px', color: '#4a5568' }}>
                              {entry.created_at
                                ? new Date(entry.created_at).toLocaleString('pl-PL', {
                                    year: 'numeric', month: '2-digit', day: '2-digit',
                                    hour: '2-digit', minute: '2-digit',
                                  })
                                : '-'}
                            </td>
                            <td style={{ padding: '10px', color: entry.quantity < 0 ? '#dc3545' : '#667eea', fontWeight: '600' }}>
                              {entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity}
                            </td>
                            <td style={{ padding: '10px', color: '#4a5568' }}>
                              {entry.user?.name || t('myProduction.unknownUser')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeRewindModal}
                  disabled={rewindModal.saving}
                  style={{
                    padding: '10px 20px', backgroundColor: '#6c757d', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleSaveRewindQuantity()}
                  disabled={saveDisabled}
                  style={{
                    padding: '10px 20px', backgroundColor: saveDisabled ? '#d3d3d3' : '#667eea',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: saveDisabled ? 'not-allowed' : 'pointer', fontWeight: '500',
                    opacity: saveDisabled ? 0.6 : 1,
                  }}
                >
                  {rewindModal.saving ? '...' : t('common.save')}
                </button>
              </div>
            </div>

            {/* Negative value confirmation — 3 s hold, like in "My production" */}
            {negativeConfirm && (
              <div
                style={{
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
                  display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000,
                }}
              >
                <div
                  style={{
                    backgroundColor: 'white', padding: '30px', borderRadius: '12px',
                    maxWidth: '450px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <h3 style={{ marginTop: 0, color: '#dc3545' }}>
                    {t('myProduction.negativeConfirmTitle')}
                  </h3>
                  <p style={{ color: '#4a5568', fontSize: '14px' }}>
                    {t('myProduction.negativeConfirmMessage', { value: rewindModal.value })}
                  </p>
                  <div style={{
                    marginTop: '15px', padding: '12px', backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb', borderRadius: '8px', color: '#721c24', fontSize: '12px',
                  }}>
                    {t('myProduction.negativeConfirmWarning')}
                  </div>
                  <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setNegativeConfirm(false);
                        handleNegativeHoldEnd();
                      }}
                      style={{
                        padding: '10px 20px', backgroundColor: '#6c757d', color: 'white',
                        border: 'none', borderRadius: '8px', cursor: 'pointer',
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                    <div style={{ position: 'relative' }}>
                      <button
                        onMouseDown={handleNegativeHoldStart}
                        onMouseUp={handleNegativeHoldEnd}
                        onMouseLeave={handleNegativeHoldEnd}
                        onTouchStart={handleNegativeHoldStart}
                        onTouchEnd={handleNegativeHoldEnd}
                        style={{
                          padding: '10px 20px', backgroundColor: '#dc3545', color: 'white',
                          border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500',
                          overflow: 'hidden', position: 'relative', userSelect: 'none',
                        }}
                      >
                        {negativeHoldProgress > 0
                          ? t('nav.holdProgress', { progress: Math.round(negativeHoldProgress).toString() })
                          : t('myProduction.negativeConfirmHoldButton')}
                      </button>
                      {negativeHoldProgress > 0 && (
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, height: '4px',
                          width: `${negativeHoldProgress}%`, backgroundColor: '#fff',
                          borderRadius: '0 0 8px 8px', transition: 'width 50ms linear',
                        }} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Confirm modal */}
      {confirm && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 420, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.confirmTitle')}
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 14 }}>
              <strong>{confirm.item.indeks_kat}</strong> &mdash; {confirm.item.material}
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 13 }}>
              {t('largeWarehouse.confirmInfo', { waga: confirm.item.waga || '', szpula: confirm.item.szpula || '' })}
            </p>
            {confirm.item.line_finished === false && (
              <p style={{
                margin: '0 0 20px 0', padding: '10px 12px', background: '#fef2f2', color: '#b91c1c',
                border: '1px solid #fecaca', borderRadius: 8, fontSize: 14, fontWeight: 600,
              }}>
                {t('largeWarehouse.planNotReached', {
                  produced: String(confirm.item.produced_on_line ?? 0),
                  planned: confirm.item.do_produkcji || '0',
                })}
              </p>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleConfirmYes}
                disabled={confirm.loading || confirm.item.line_finished === false}
                style={{
                  flex: 1, padding: '13px 0', background: '#10b981', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                  cursor: confirm.item.line_finished === false ? 'not-allowed' : 'pointer',
                  opacity: confirm.loading || confirm.item.line_finished === false ? 0.6 : 1,
                }}
              >
                {confirm.loading ? '...' : `✔ ${t('largeWarehouse.confirmYes')}`}
              </button>
              <button
                onClick={() => setConfirm(null)}
                disabled={confirm.loading}
                style={{
                  flex: 1, padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('largeWarehouse.confirmNo')}
              </button>
            </div>
            {/* Pomyłkowo wysłana na stację pozycja wraca do „Mojej produkcji" (tylko admin). */}
            {canRestoreRewinding && (
              <button
                onClick={() => { const it = confirm.item; setConfirm(null); setRemoveItem(it); }}
                style={{
                  marginTop: 12, width: '100%', padding: '11px 0', background: 'transparent',
                  color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 8,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                ↩️ {t('largeWarehouse.removeConfirm')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cofnięcie oczekującej pozycji ze stacji do „Mojej produkcji" */}
      {removeItem && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>↩️</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.removeTitle')}
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 14 }}>
              <strong>{removeItem.indeks_kat}</strong> &mdash; {removeItem.material}
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 13 }}>
              {t('largeWarehouse.removeBody')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleRemoveConfirm}
                disabled={removing}
                style={{
                  flex: 1, padding: '13px 0', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                  cursor: removing ? 'not-allowed' : 'pointer', opacity: removing ? 0.6 : 1,
                }}
              >
                {removing ? '...' : `↩️ ${t('largeWarehouse.removeConfirm')}`}
              </button>
              <button
                onClick={() => setRemoveItem(null)}
                disabled={removing}
                style={{
                  flex: 1, padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                  border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Po osiągnięciu planu: oznaczyć jako przewiniętą (→ zielona) albo przewijać dalej (→ niebieska). */}
      {markRewoundChoice && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.markRewoundPromptTitle')}
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 14 }}>
              <strong>{markRewoundChoice.indeks_kat}</strong> &mdash; {markRewoundChoice.material}
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 13 }}>
              {t('largeWarehouse.markRewoundPromptBody')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => { const it = markRewoundChoice; setMarkRewoundChoice(null); markAsRewound(it); }}
                style={{
                  flex: 1, padding: '13px 0', background: '#10b981', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                }}
              >
                ✔ {t('common.yes')}
              </button>
              <button
                onClick={() => setMarkRewoundChoice(null)}
                style={{
                  flex: 1, padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                  border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('largeWarehouse.markRewoundContinue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Uwagi stacji przewijania — osobne od uwag linii */}
      {notesItem && (
        <StationNotesModal
          warehouseItemId={notesItem.id}
          itemLabel={`${notesItem.indeks_kat || ''} — ${notesItem.material || ''}`}
          currentNote={notesItem.notes}
          noteBy={notesItem.notes_by}
          noteAt={notesItem.notes_at}
          problemTypes={problemTypes}
          dateLocale={dateLocale}
          onClose={() => setNotesItem(null)}
          onSaved={fetchItems}
        />
      )}

      {/* Przywrócenie pozycji z „Przewinięte" z powrotem do przewijania */}
      {restoreItem && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>↩️</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.restoreTitle')}
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 14 }}>
              <strong>{restoreItem.indeks_kat}</strong> &mdash; {restoreItem.material}
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 13 }}>
              {t('largeWarehouse.restoreBody')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleRestoreConfirm}
                disabled={restoring}
                style={{
                  flex: 1, padding: '13px 0', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                  cursor: restoring ? 'not-allowed' : 'pointer', opacity: restoring ? 0.6 : 1,
                }}
              >
                {restoring ? '...' : `↩️ ${t('largeWarehouse.restoreConfirm')}`}
              </button>
              <button
                onClick={() => setRestoreItem(null)}
                disabled={restoring}
                style={{
                  flex: 1, padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                  border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
